import React, { useState, useEffect } from 'react';
import Sidebar from '../Sidebar';
import { ArrowRight, Truck, LayoutDashboard, PackageOpen } from 'lucide-react';
import HelpCenter from '../HelpCenter';
import ModuloHeader from '../ModuloHeader';
import RetiradaAcompanhamento from './retirada/RetiradaAcompanhamento';
import { perfilNoModulo } from '../../utils/perfisDerivados';

// =====================================================================
// Hub do Módulo LOGÍSTICA. Paleta PETRÓLEO #0e7490.
//
// O QUE É: o pedido que faz alguma coisa sair da unidade e voltar. Um programa
// — a **Retirada de Equipamento** —, e dentro dele a EQUIPE decide a forma:
//   T.I        → sai o EQUIPAMENTO (com comprovante em PDF);
//   Manutenção → saem as PEÇAS de um reparo.
//
// ⚠️⚠️ DE ONDE VEIO (10/09): este módulo é a FUSÃO de duas coisas que viviam
//    separadas. A **Retirada de Equipamento** era uma aba do CHAMADOS e um card
//    do hub da MANUTENÇÃO; a **Solicitação de Reparo** era um programa do
//    MANUTENÇÃO. As duas sempre foram o mesmo pedido com formas diferentes, e
//    ter três donos é como as metades divergem.
//    ⚠️ A Solicitação de Reparo **não existe mais** no módulo Manutenção — ela é
//    a metade "Manutenção" desta tela.
//
// ⚠️ HUB COM UM CARD SÓ, e é de propósito: o padrão dos hubs do portal (barra de
//    navegação + Visão Geral + cards) é o que faz a pessoa reconhecer onde está.
//    Abrir direto na tela pouparia um clique hoje e exigiria refazer a navegação
//    no dia do segundo programa.
//
// ⚠️⚠️ A ESTEIRA NÃO ESTÁ AQUI. Quem lança no SAP, informa a nota, despacha,
//    prevê e recebe é o **Compras** (com o Fiscal na nota), no hub deles —
//    decisão da Julia em 10/09. Aqui se ABRE a retirada, se imprime o
//    comprovante e se ACOMPANHA. Botão de avançar nesta tela devolveria 403,
//    porque quem decide o dono de cada passo é a máquina de estados no backend.
// =====================================================================

const COR = '#0e7490';
const COR_ESCURA = '#155e75';
const COR_CLARA = '#ecfeff';
const COR_BORDA = '#cffafe';

const PERMISSIONS = {
  // ⚠️⚠️ A LISTA TEM DE CABER NO `PERFIS` DO SERVIDOR
  //    (`server/logistica/server-logistica.js`). Perfil aqui e fora de lá dá um
  //    card que ABRE e devolve 403 em tudo — e o sintoma ("a tela não funciona
  //    para mim") não aponta para a causa. Há teste comparando os dois.
  // ⚠️ `Gerente` entra: a loja também pede que levem o equipamento parado. O que
  //    o prende à própria unidade NÃO é esta lista — é o `podeVerLoja` da
  //    validação no servidor, na ESCRITA.
  // ⚠️ `Gerente Plantao` fica FORA, como no resto do portal para atos que
  //    comprometem patrimônio: retirar equipamento gera comprovante assinado.
  'retirada': ['Admin', 'Logistica', 'Manutencao', 'Diretoria', 'Gerente', 'Gerente TI', 'TI', 'Operacional'],
};

const modulesList = [
  {
    id: 'retirada',
    label: 'Solicitação de Retirada de equipamento',
    icon: PackageOpen,
    color: COR,
    bgColor: COR_CLARA,
    desc: 'A saída de um equipamento (T.I) ou as peças de um reparo (Manutenção) — com o comprovante em PDF e o acompanhamento do retorno',
    stat: 'T.I e Manutenção',
  },
];

const LogisticaPainel = () => {
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : {};
  const userRole = perfilNoModulo(user?.perfil, 'logistica') || '';

  const [isMobile] = useState(window.innerWidth <= 768);
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const allowedModules = modulesList.filter(
    (mod) => PERMISSIONS[mod.id]?.includes(userRole) || userRole === 'Admin'
  );

  const OverviewWidgets = () => (
    <div>
      <div style={{
        background: `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)`,
        borderRadius: '16px', padding: '40px', color: '#fff',
        marginBottom: '32px', position: 'relative', overflow: 'hidden',
        boxShadow: '0 10px 15px -3px rgba(14, 116, 144, 0.45)',
      }}>
        <div style={{ position: 'relative', zIndex: 10 }}>
          <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px', marginTop: 0, color: '#fff' }}>
            Logística
          </h2>
          <p style={{ color: COR_CLARA, fontSize: '18px', maxWidth: '650px', margin: 0, lineHeight: '1.6' }}>
            O que se move entre as unidades e o escritório — a saída do equipamento,
            o comprovante que o acompanha e o retorno.
          </p>
        </div>
        <div style={{ position: 'absolute', right: '-30px', top: '-40px', opacity: 0.12 }}>
          <Truck size={250} color="#ffffff" />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
        {allowedModules.map((mod) => (
          <div
            key={mod.id}
            onClick={() => setActiveTab(mod.id)}
            style={{
              backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
              border: `1px solid ${COR_BORDA}`, display: 'flex', flexDirection: 'column',
              height: '100%', cursor: 'pointer', position: 'relative', overflow: 'hidden',
              transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px -2px rgba(0,0,0,0.1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)'; }}
          >
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: mod.color }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <p style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: mod.color, marginBottom: '8px', margin: 0 }}>{mod.stat}</p>
                <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 }}>{mod.label}</h3>
              </div>
              <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: mod.bgColor, color: mod.color }}>
                <mod.icon size={26} />
              </div>
            </div>
            <div style={{ marginTop: 'auto' }}>
              <p style={{ fontSize: '14px', color: '#64748b', fontWeight: '500', lineHeight: '1.5', margin: '0 0 16px 0' }}>{mod.desc}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '700', color: mod.color }}>
                <span>Acessar</span> <ArrowRight size={16} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ⚠️ Diz onde está a esteira, em vez de deixar a pessoa procurar. Quem
          abre a retirada quase sempre quer saber "e agora, quem toca?" — e a
          resposta é outro módulo. Sem esta linha, o caminho fácil é concluir
          que a tela está incompleta. */}
      <p style={{
        marginTop: 24, padding: '12px 14px', background: COR_CLARA,
        border: `1px solid ${COR_BORDA}`, borderRadius: 10,
        fontSize: 13.5, color: '#155e75', lineHeight: 1.6,
      }}>
        <strong>Depois de registrada</strong>, a solicitação segue no módulo <strong>Compras</strong>:
        é lá que ela é lançada no SAP, o Fiscal informa a nota, o item é despachado ao
        fornecedor e o retorno é registrado. Aqui você acompanha o andamento e reimprime
        o comprovante quando precisar.
      </p>
    </div>
  );

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (activeTab !== 'dashboard' && !(PERMISSIONS[activeTab]?.includes(userRole) || userRole === 'Admin')) {
      setActiveTab('dashboard');
    }
  }, [activeTab, userRole]);

  const contentBoxStyle = {
    backgroundColor: '#fff', borderRadius: '16px', border: `1px solid ${COR_BORDA}`,
    padding: '32px', minHeight: '75vh', width: '100%',
  };

  const renderContent = () => {
    if (activeTab === 'dashboard') return <OverviewWidgets />;
    // ⚠️ A alternância entre a lista e o formulário é da PRÓPRIA tela
    //    (`RetiradaAcompanhamento` tem o estado `tela`), e não do hub. Guardá-la
    //    aqui obrigaria o hub a saber que existe um formulário lá dentro — e a
    //    tela já resolve o caso que importa: gravar volta para a lista e
    //    recarrega.
    if (activeTab === 'retirada') {
      return <div style={contentBoxStyle}><RetiradaAcompanhamento /></div>;
    }
    return null;
  };

  const getMarginLeft = () => (window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px'));

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8f8f8' }}>
      <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />
      <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease' }}>

        <ModuloHeader
          eyebrow="Módulo"
          titulo="Logística"
          cor={COR}
          onAbrirManual={() => setIsHelpOpen(true)}
          onAbrirMenu={() => setIsCollapsed(false)}
          isMobile={isMobile}
        />

        <div style={{ padding: '40px', maxWidth: '1600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

          {/* BARRA DE NAVEGAÇÃO */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '32px',
            backgroundColor: '#fff', padding: '8px', borderRadius: '12px',
            border: `1px solid ${COR_BORDA}`, width: '100%',
          }}>
            <button
              onClick={() => setActiveTab('dashboard')}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 20px', borderRadius: '8px', border: 'none',
                fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                background: activeTab === 'dashboard' ? `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)` : 'transparent',
                color: activeTab === 'dashboard' ? '#fff' : '#64748b',
                boxShadow: activeTab === 'dashboard' ? '0 4px 6px rgba(14, 116, 144, 0.4)' : 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <LayoutDashboard size={18} /> Visão Geral
            </button>

            <div style={{ minWidth: '1px', backgroundColor: COR_BORDA, margin: '0 5px' }} />

            {allowedModules.map((mod) => (
              <button
                key={mod.id}
                onClick={() => setActiveTab(mod.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 20px', borderRadius: '8px', fontWeight: '600',
                  cursor: 'pointer', transition: 'all 0.2s',
                  border: activeTab === mod.id ? `2px solid ${mod.color}` : '2px solid transparent',
                  backgroundColor: activeTab === mod.id ? mod.bgColor : 'transparent',
                  color: activeTab === mod.id ? mod.color : '#64748b',
                  whiteSpace: 'nowrap',
                }}
              >
                <mod.icon size={18} /> {mod.label}
              </button>
            ))}
          </div>

          {renderContent()}
        </div>
      </div>

      {/* Manual da Tela — verbetes do módulo (HelpCenter › manualLogistica). */}
      <HelpCenter
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        modulo="logistica"
        secaoInicial={activeTab === 'retirada' ? 'logistica-retirada' : 'visao-geral-logistica'}
      />
    </div>
  );
};

export default LogisticaPainel;
